/**
 * useSendMonthlyReports — S10-05, erweitert in R3/Stream 1
 *
 * Mutation hook for POST /api/v2/monthly_reports/send_now
 *
 * FIN-SWEEP-FE-3 (F11): Routed via lib/api-client.ts so the Demo-Token-aware
 * Authorization header is set automatically (lib/api-client.ts:75-102).
 *
 * R3/FE-4 (v1-Paritaet zu ReportsController#deliver_email): Der Endpoint
 * akzeptiert jetzt zusaetzlich `month`, `time_zone` und `recipients`
 * ({ account_email, contacts: { <id>: email } }). Der Aufruf bleibt
 * rueckwaertskompatibel:
 *   sendNow()            -> {}                         (alle aktivierten Kontakte)
 *   sendNow([1, 2])      -> { contact_ids: [1, 2] }     (Legacy-Pfad)
 *   sendNow({ month, timeZone, recipients })            (v1-Versand-Dialog)
 */
import { useState, useCallback } from 'react'
import apiClient from '../../lib/api-client'
import i18n from '../../i18n'

// /api/v2/monthly_reports/send_now is not in @heliumtracker/api-types yet,
// hence the `as any` cast (same pattern as usePayments.ts:42-45).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const client = apiClient as any

export type SendMonthlyReportsResult =
  | { ok: true; queued: number; message: string; recipients: string[] }
  | { ok: false; error: string; retryAfter?: number }

/** v1-Versand-Dialog: frei editierbare Empfaengeradressen (Account + Kontakte). */
export type SendRecipients = {
  /** Full-Account-Report an diese Adresse; null/undefined = nicht versenden. */
  accountEmail?: string | null
  /** contactId -> Zieladresse */
  contacts?: Record<number, string>
}

export type SendMonthlyReportsOptions = {
  /** "YYYY-MM" — Default (BE): laufender Monat */
  month?: string
  /** Rails-Zonenname aus GET /api/v2/reports/time_zones */
  timeZone?: string
  recipients?: SendRecipients
  /** Legacy-Pfad (nur wenn keine `recipients` gesetzt sind) */
  contactIds?: number[]
}

export type SendNowArg = number[] | SendMonthlyReportsOptions | undefined

interface ApiErrorBody {
  status?: number
  body?: { message?: string; error?: string; retry_after?: number; limit?: number } | string
}

/** Baut den Request-Body — additiv und rueckwaertskompatibel. */
export function buildSendNowBody(arg: SendNowArg): Record<string, unknown> {
  if (arg === undefined) return {}
  if (Array.isArray(arg)) return { contact_ids: arg }

  const body: Record<string, unknown> = {}
  if (arg.month) body.month = arg.month
  if (arg.timeZone) body.time_zone = arg.timeZone

  if (arg.recipients) {
    const recipients: Record<string, unknown> = {}
    if (arg.recipients.accountEmail) recipients.account_email = arg.recipients.accountEmail
    const contacts = arg.recipients.contacts ?? {}
    const contactEntries = Object.entries(contacts).filter(([, email]) => !!email)
    if (contactEntries.length > 0) {
      recipients.contacts = Object.fromEntries(contactEntries)
    }
    // Nur senden, wenn wirklich Empfaenger gewaehlt wurden — ein leeres
    // `recipients` wuerde im BE den Legacy-Pfad ausloesen (params[:recipients]
    // blank) und damit still an ALLE aktivierten Kontakte gehen.
    if (Object.keys(recipients).length > 0) body.recipients = recipients
  } else if (arg.contactIds) {
    body.contact_ids = arg.contactIds
  }

  return body
}

function bodyErrorCode(body: ApiErrorBody['body']): string | undefined {
  if (typeof body === 'object' && body !== null) return body.error
  return undefined
}

/** Uebersetzt die BE-Fehlercodes des erweiterten Endpoints in i18n-Texte. */
function messageForError(status: number | undefined, body: ApiErrorBody['body']): SendMonthlyReportsResult {
  const code = bodyErrorCode(body)
  const retryAfter = typeof body === 'object' && body !== null ? body.retry_after : undefined

  if (status === 429) {
    if (code === 'daily_limit_exceeded') {
      const limit = typeof body === 'object' && body !== null ? body.limit : undefined
      return { ok: false, error: i18n.t('reports:sendDialog.errors.dailyLimit', { limit: limit ?? 20 }) }
    }
    return { ok: false, error: i18n.t('reports:sendNow.rateLimited'), retryAfter }
  }
  if (status === 422) {
    if (code === 'invalid_month') return { ok: false, error: i18n.t('reports:sendDialog.errors.invalidMonth') }
    if (code === 'invalid_recipient') return { ok: false, error: i18n.t('reports:sendDialog.errors.invalidRecipient') }
    if (code === 'too_many_recipients') {
      const limit = typeof body === 'object' && body !== null ? body.limit : undefined
      return { ok: false, error: i18n.t('reports:sendDialog.errors.tooManyRecipients', { limit: limit ?? 20 }) }
    }
  }
  if (status === 404) return { ok: false, error: i18n.t('reports:sendDialog.errors.unknownContact') }

  return { ok: false, error: i18n.t('reports:sendNow.error') }
}

async function sendMonthlyReportsRequest(arg: SendNowArg): Promise<SendMonthlyReportsResult> {
  const body = buildSendNowBody(arg)

  try {
    const result = await client.POST('/api/v2/monthly_reports/send_now', { body })

    if (result.error) {
      const err = result.error as ApiErrorBody
      return messageForError(err?.status, err?.body)
    }

    const json = result.data as {
      data?: { queued?: number; message?: string; recipients?: string[] }
    }

    return {
      ok: true,
      queued: json?.data?.queued ?? 0,
      message: json?.data?.message ?? '',
      recipients: json?.data?.recipients ?? [],
    }
  } catch (e) {
    const err = e as ApiErrorBody
    return messageForError(err?.status, err?.body)
  }
}

export function useSendMonthlyReports() {
  const [isPending, setIsPending] = useState(false)

  const sendNow = useCallback(async (arg?: SendNowArg): Promise<SendMonthlyReportsResult> => {
    setIsPending(true)
    try {
      return await sendMonthlyReportsRequest(arg)
    } finally {
      setIsPending(false)
    }
  }, [])

  return { sendNow, isPending }
}
